/** @jsxImportSource @emotion/react */

import * as React from 'react'
import { css, jsx } from '@emotion/react'
import { Select } from '../select/select'
import { Primary, mixins } from '@sphinx/components-styles'
import { Button, ButtonType } from '../button/button'
import {
  PageFirstIcon,
  PageLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '../icons'

interface PaginationProps {
  pages: number
  disabled?: boolean
  currentPage?: number
  selectOptions?: React.ComponentProps<typeof Select>
  onChange?(page: number): void
}

const GhostButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  ...props
}) => (
  <Button buttonType={ButtonType.GHOST} {...props}>
    {children}
  </Button>
)

const toSelectOption = (value: number) => ({
  value,
  label: value,
})

const getPaginationOptions = (pages: number) =>
  Array(pages)
    .fill(1)
    .map((item, index) => item + index)
    .map(toSelectOption)

export const Pagination: React.FC<PaginationProps> = ({
  pages,
  disabled,
  currentPage = 1,
  selectOptions = {},
  onChange = () => {},
}) => (
  <div
    css={css`
      display: flex;
      align-items: center;
    `}
  >
    <GhostButton
      disabled={disabled || currentPage === 1}
      onClick={() => onChange(1)}
      aria-label="go to the first page"
    >
      <PageFirstIcon />
    </GhostButton>
    <GhostButton
      disabled={disabled || currentPage === 1}
      onClick={() => onChange(--currentPage)}
      aria-label="go to the previous page"
    >
      <ChevronLeftIcon />
    </GhostButton>
    <div
      css={css`
        margin: 0 8px;
        display: flex;
        align-items: center;
      `}
    >
      <Select
        placeholder=""
        isDisabled={disabled}
        aria-label="select page"
        value={toSelectOption(currentPage)}
        options={getPaginationOptions(pages)}
        onChange={({ value }: any) => onChange(value)}
        menuPlacement="auto"
        styles={{
          container: (base) => ({
            ...base,
            width: '64px',
          }),
        }}
        {...selectOptions}
      />
      <span
        css={css`
          ${mixins.menu};
          color: ${Primary.navy[100]};
          margin-left: 8px;
          ${disabled &&
          css`
            opacity: 0.2;
          `}
        `}
      >
        of {pages}
      </span>
    </div>
    <GhostButton
      disabled={disabled || currentPage === pages}
      onClick={() => onChange(++currentPage)}
      aria-label="go to the next page"
    >
      <ChevronRightIcon />
    </GhostButton>
    <GhostButton
      disabled={disabled || currentPage === pages}
      onClick={() => onChange(pages)}
      aria-label="go to the last page"
    >
      <PageLastIcon />
    </GhostButton>
  </div>
)
